"use client";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { postData } from "@/api/api";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import ErrorMsg from "../common/error-msg";
import { useCookies } from "next-client-cookies";

const LoginForm = () => {
  const cookies = useCookies();
  const [loading, setLoading] = useState<boolean>(false);
  const [showOtpScreen, setShowOtpScreen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [timer, setTimer] = useState<any>(30);

  type FormData = { email: string; otp: string; };

  let schema: any;
  schema = showOtpScreen
    ? yup.object().shape({ email: yup.string().required().email().label("Email"), otp: yup.string().label("Otp") })
    : yup.object().shape({ email: yup.string().required().email().label("Email") });

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({ resolver: yupResolver(schema) });
  const router = useRouter();

  useEffect(() => { if (timer < 1) setTimer(0); }, [timer]);

  useEffect(() => {
    let interval: any;
    if (showOtpScreen === true && timer < 31) {
      interval = setInterval(() => setTimer((prev: any) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [showOtpScreen, timer]);

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    try {
      const res = await postData("/auth/send-login-otp", { email: data?.email });
      if (!res?.success) { setToastMessage(res?.message || "OTP sending failed."); return; }
      setToastMessage(res?.message);
      setShowOtpScreen(true);
      setTimer(30);
    } catch (e) { setToastMessage((e as string) || "Server Error"); }
    finally { setLoading(false); }
  });

  const onSubmitOtp = handleSubmit(async (data) => {
    setLoading(true);
    try {
      const res = await postData("/auth/login-with-otp", { email: data?.email, otp: Number(data?.otp) });
      if (!res?.success) { setToastMessage(res?.message || "Login failed."); return; }
      setToastMessage(res?.message);
      const { accessToken, ...otherinfo } = res?.data;
      cookies.set("token", accessToken);
      cookies.set("userinfo", JSON.stringify(otherinfo));
      router.push("/");
    } catch (e) { setToastMessage((e as string) || "Server Error"); }
    finally { setLoading(false); }
  });

  return (
    <div className="lf-wrap">
      {/* LEFT HERO */}
      <div className="lf-hero">
        <div className="lf-wave lf-wave-1" />
        <div className="lf-wave lf-wave-2" />
        <div className="lf-wave lf-wave-3" />
        <div className="lf-fc lf-fc1" />
        <div className="lf-fc lf-fc2" />
        <div className="lf-fc lf-fc3" />

        <div className="lf-brand">
          <div className="lf-brand-icon">
            <svg viewBox="0 0 24 24" fill="white" width="22" height="22">
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59L5.25 14c-.16.28-.25.61-.25.96C5 16.1 5.9 17 7 17h14v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63H19c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1 1 0 0023.75 5H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2S15.9 22 17 22s2-.9 2-2-.9-2-2-2z"/>
            </svg>
          </div>
          <span className="lf-brand-name">SHOPNOW</span>
        </div>

        <div className="lf-hero-text">
          <h2>Hello,</h2>
          <h2>Welcome Back!</h2>
          <p>Sign in to continue shopping<br/>your favourite products</p>
        </div>
      </div>

      {/* RIGHT FORM */}
      <div className="lf-form-panel">
        <div className="lf-form-inner">
          <h3 className="lf-form-title">Welcome</h3>
          <p className="lf-form-sub">Login to your account to continue</p>

          {toastMessage && <div className="lf-toast">{toastMessage}</div>}

          <form onSubmit={!showOtpScreen ? onSubmit : onSubmitOtp}>
            <div className="lf-field">
              <input {...register("email")} type="email" placeholder="Email" disabled={showOtpScreen} className="lf-input" />
              <ErrorMsg msg={errors.email?.message!} />
            </div>

            {showOtpScreen && (
              <div className="lf-field">
                <input {...register("otp")} type="text" placeholder="Enter OTP" maxLength={6} className="lf-input" />
                <ErrorMsg msg={errors.otp?.message!} />
              </div>
            )}

            {showOtpScreen && timer < 31 && (
              <div className="lf-resend">
                <span className={timer > 0 ? "lf-resend-disabled" : "lf-resend-link"} onClick={() => timer > 0 || loading ? null : onSubmit()}>
                  {timer > 0 ? `Resend OTP in ${timer}s` : loading ? "Sending..." : "Resend OTP"}
                </span>
              </div>
            )}

            {!showOtpScreen && (
              <div className="lf-forgot"><a href="#">Forgot your password?</a></div>
            )}

            <button type="submit" className="lf-btn" disabled={loading}>
              {loading ? (showOtpScreen ? "Please wait..." : "Sending OTP...") : (showOtpScreen ? "LOGIN" : "SEND OTP")}
            </button>

            <p className="lf-signup">
              Don&apos;t have an account?{" "}
              <Link href="/register" className="lf-signup-link">Sign Up</Link>
            </p>
          </form>
        </div>
      </div>

      <style jsx>{`
        .lf-wrap {
          display: flex;
          width: 100%;
          min-height: 480px;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 24px 64px rgba(161, 161, 161, 0.18), 0 4px 20px rgba(0,0,0,0.08);
          font-family: 'Poppins', 'Segoe UI', sans-serif;
        }

        /* HERO */
        .lf-hero {
          flex: 1;
          background: linear-gradient(155deg, #ffb347 0%, #ff9966 30%, #ff6b35 65%, #ff5e62 100%);
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 36px 32px 40px;
          min-width: 0;
        }
        .lf-wave {
          position: absolute;
          border-radius: 50%;
          background: rgba(255,255,255,0.1);
          pointer-events: none;
        }
        .lf-wave-1 { width: 300px; height: 300px; top: -100px; left: -80px; animation: wv 7s ease-in-out infinite; }
        .lf-wave-2 { width: 220px; height: 220px; bottom: -60px; right: -60px; background: rgba(255,255,255,0.08); animation: wv 5s ease-in-out infinite reverse; }
        .lf-wave-3 { width: 160px; height: 160px; top: 40%; left: 50%; background: rgba(255,255,255,0.06); animation: wv 9s ease-in-out infinite 1s; }
        @keyframes wv { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-16px) scale(1.05)} }

        .lf-fc { position: absolute; border-radius: 50%; pointer-events: none; }
        .lf-fc1 { width: 80px; height: 80px; top: 20%; right: 10%; background: rgba(255,255,255,0.12); animation: wv 4s ease-in-out infinite; }
        .lf-fc2 { width: 50px; height: 50px; bottom: 25%; left: 15%; background: rgba(255,255,255,0.1); animation: wv 6s ease-in-out infinite reverse; }
        .lf-fc3 { width: 30px; height: 30px; top: 60%; right: 20%; background: rgba(255,255,255,0.15); animation: wv 3.5s ease-in-out infinite 0.5s; }

        .lf-brand { display: flex; align-items: center; gap: 10px; position: relative; z-index: 2; }
        .lf-brand-icon {
          width: 42px; height: 42px;
          background: rgba(255,255,255,0.2); border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          backdrop-filter: blur(6px); border: 1px solid rgba(255,255,255,0.3);
        }
        .lf-brand-name { font-size: 15px; font-weight: 800; color: #fff; letter-spacing: 2px; }

        .lf-hero-text { position: relative; z-index: 2; }
        .lf-hero-text h2 { font-size: 28px; font-weight: 800; color: #fff; line-height: 1.2; margin: 0; text-shadow: 0 2px 12px rgba(0,0,0,0.1); }
        .lf-hero-text p { font-size: 13px; color: rgba(255,255,255,0.78); margin-top: 10px; line-height: 1.6; }

        /* FORM PANEL */
        .lf-form-panel {
          flex: 1; background: #fff;
          display: flex; align-items: center; justify-content: center;
          padding: 40px 32px; min-width: 0;
        }
        .lf-form-inner { width: 100%; max-width: 300px; }
        .lf-form-title { font-size: 26px; font-weight: 800; color: #ff6b35; text-align: center; margin-bottom: 6px; }
        .lf-form-sub { font-size: 13px; color: #aaa; text-align: center; margin-bottom: 28px; }

        .lf-toast {
          background: #fff5f0; color: #ff6b35; border: 1px solid #ffd4c0;
          border-radius: 10px; padding: 10px 14px; font-size: 13px;
          font-weight: 600; text-align: center; margin-bottom: 16px;
          animation: toastIn 0.3s ease;
        }
        @keyframes toastIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }

        .lf-field { margin-bottom: 16px; }
        .lf-input {
          width: 100%; padding: 13px 16px; border-radius: 10px;
          border: 1.5px solid #efefef; background: #fafafa;
          font-size: 14px; color: #333; font-family: inherit;
          transition: all 0.25s; outline: none;
        }
        .lf-input:focus { border-color: #ff9966; background: #fff; box-shadow: 0 0 0 3px rgba(255,153,102,0.15); }
        .lf-input:disabled { background: #f0f0f0; color: #bbb; cursor: not-allowed; }
        .lf-input::placeholder { color: #ccc; }

        .lf-forgot { text-align: right; margin-bottom: 20px; }
        .lf-forgot a { font-size: 12px; color: #aaa; text-decoration: none; }
        .lf-forgot a:hover { color: #ff6b35; }

        .lf-resend { text-align: right; margin-bottom: 16px; font-size: 12px; }
        .lf-resend-link { color: #ff6b35; font-weight: 700; cursor: pointer; }
        .lf-resend-disabled { color: #bbb; cursor: default; }

        .lf-btn {
          width: 100%; padding: 14px; border-radius: 12px; border: none;
          background: linear-gradient(90deg, #ff9966, #ff5e62);
          color: #fff; font-size: 14px; font-weight: 800; letter-spacing: 1.5px;
          cursor: pointer; font-family: inherit; transition: all 0.3s;
          box-shadow: 0 6px 20px rgba(255,94,98,0.3); margin-bottom: 20px;
        }
        .lf-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .lf-btn:active { transform: scale(0.98); }
        .lf-btn:disabled { background: #ddd; color: #999; box-shadow: none; cursor: not-allowed; transform: none; }

        .lf-signup { text-align: center; font-size: 13px; color: #aaa; }
        .lf-signup-link { color: #ff6b35; font-weight: 700; text-decoration: none; }

        /* MOBILE */
        @media (max-width: 640px) {
          .lf-wrap { flex-direction: column; min-height: unset; border-radius: 28px; }
          .lf-hero { min-height: 220px; padding: 28px 24px 36px; }
          .lf-hero-text h2 { font-size: 22px; }
          .lf-form-panel { padding: 28px 24px 32px; }
          .lf-form-inner { max-width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default LoginForm;